import clsx from "clsx";
import React from "react";
import ReactLoading from "react-loading";

import { CheckIcon, ShieldCheckIcon, ShieldExclamationIcon } from "@heroicons/react/24/outline";


type DemoCardProps = {
  className?: string;
  title?: string;
  code?: string;
  badgeLabel?: string;
  badgeIcon?: "check" | "exclamation";
  rationaleTitle?: string;
  rationaleText?: string;
  rationaleList?: Array<{ id: string; text: string }>; // NEW: optional bullet list
  onRationaleHover?: (id: string | null) => void; // NEW: hover callback
  secondarySectionTitle?: string;
  secondarySectionText?: string;
  showDividerAfterCode?: boolean;
  showActions?: boolean;
  showTags?: boolean;
  penaltyText?: string;
  bottomTagText?: string;
  meatValidatedLetters?: Array<"M" | "E" | "A" | "T">;
  primaryActionLabel?: string;
  secondaryActionLabel?: string;
  onPrimary?: () => void;
  onSecondary?: () => void;
  accent?: boolean;
  accentColor?: string;
};

/**
 * Main demo card used in the hero animation next to the orb.
 * Defaults mirror the "Add Dx" card from the mock.
 *
 * This module also exports related "support" UI elements used near the card:
 * - DemoCardSupportBar (MEAT + penalty chip)
 * - DemoCardTag (small pill label e.g., Mid-year run)
 * - DemoCardMini (compact action card; optional)
 */
export const DemoCard: React.FC<DemoCardProps> = ({
  className,
  title = "Add Dx",
  code = "E11.9 | HCC 38",
  badgeLabel = "",
  badgeIcon = "check",
  rationaleTitle = "Rationale",
  rationaleText = "01-12 · PRIMARY CARE · J. Smith, DO · Riverside IM\nCC: [Shortness of breath]; [diabetes follow-up]\nHPI: T2DM (poor control); [↑HFrEF]; [CKD 3b]; HTN; HLD; COPD",
  rationaleList,
  onRationaleHover,
  secondarySectionTitle,
  secondarySectionText,
  showDividerAfterCode = true,
  showActions = true,
  showTags = true,
  penaltyText = "+$144.00",
  bottomTagText = "Mid-year run",
  meatValidatedLetters = ["M", "A"],
  primaryActionLabel = "Add Dx",
  secondaryActionLabel = "Reject",
  onPrimary,
  onSecondary,
  accent = false,
  accentColor = "#FF8000",
}) => {
  const hexToRgb = (hex: string) => {
    const v = hex.replace("#", "");
    const n = parseInt(v.length === 3 ? v.split("").map(c => c + c).join("") : v, 16);
    const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    return { r, g, b };
  };
  const rgba = (hex: string, a: number) => {
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  };
  const main = accent ? accentColor : "#FFFFFF";
  const weak = rgba(main, 0.2);
  const mid = rgba(main, 0.6);
  const soft = rgba(main, 0.85);
  const strong = rgba(main, 0.9);

  return (
    <div
      className={clsx(
        "relative w-[260px] rounded-lgx border border-white/20 bg-transparent text-white/90 font-light",
        className
      )}
      style={{ borderColor: accent ? weak : undefined }}
      role="region"
      aria-label="Demo Card"
    >
      {showTags ? (
        <>
          <DemoCardTag
            variant="meat"
            validatedLetters={meatValidatedLetters}
            className="absolute -top-[34px] -left-[9px]"
            accent={accent}
            accentColor={accentColor}
          />
          <DemoCardTag
            variant="pill"
            className="absolute -top-[34px] -right-[1px]"
            accent={accent}
            accentColor={accentColor}
          >
            {penaltyText}
          </DemoCardTag>
          <DemoCardTag
            variant="pill"
            className="absolute -bottom-[34px] -right-[1px]"
            accent={accent}
            accentColor={accentColor}
          >
            {bottomTagText}
          </DemoCardTag>
        </>
      ) : null}
      <div className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="inline-block h-1.5 w-1.5 rounded-[2px]x" style={{ border: `1px solid ${accent ? mid : "rgba(255,255,255,0.4)"}`, backgroundColor: accent ? main : "#FFFFFF" }} />
          <span className="text-[13px] leading-4" style={{ color: accent ? strong : undefined }}>{title}</span>
        </div>

        <div className="flex items-center justify-between mb-2">
          <div className="text-[13px] tracking-tight" style={{ color: accent ? main : undefined }}>{code}</div>
          {badgeLabel ? (
            <span className="ml-2 shrink-0 rounded-smx bg-transparent px-1.5 py-1 text-[10px] leading-3 inline-flex items-center gap-1"
                  style={{ color: accent ? strong : undefined, border: `1px solid ${accent ? weak : "rgba(255,255,255,0.2)"}` }}>
              {(badgeIcon === "exclamation" ? <ShieldExclamationIcon className="h-3 w-3" /> : <ShieldCheckIcon className="h-3 w-3" />)}
              {badgeLabel}
            </span>
          ) : null}
        </div>
        {showDividerAfterCode ? <div className="h-px w-full" style={{ backgroundColor: accent ? mid : "rgba(255,255,255,0.6)" }} /> : null}

        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wide mb-1" style={{ color: accent ? mid : "rgba(255,255,255,0.6)" }}>{rationaleTitle}</div>
          {rationaleList && rationaleList.length > 0 ? (
            <ul className="space-y-1">
              {rationaleList.map((r) => (
                <li
                  key={r.id}
                  className="text-[12px] leading-4"
                  style={{ color: accent ? soft : "rgba(255,255,255,0.85)" }}
                  onMouseEnter={() => onRationaleHover?.(r.id)}
                  onMouseLeave={() => onRationaleHover?.(null)}
                >
                  • {r.text}
                </li>
              ))}
            </ul>
          ) : (
            <div className="whitespace-pre-wrap text-[12px] leading-4" style={{ color: accent ? soft : "rgba(255,255,255,0.85)" }}>
              {rationaleText}
            </div>
          )}
        </div>
        <div className="h-px w-full mt-3" style={{ backgroundColor: accent ? mid : "rgba(255,255,255,0.6)" }} />

        {secondarySectionTitle ? (
          <div className="mt-3">
            <div className="text-[11px] uppercase tracking-wide mb-1" style={{ color: accent ? mid : "rgba(255,255,255,0.6)" }}>
              {secondarySectionTitle}
            </div>
            <div className="text-[12px] leading-4" style={{ color: accent ? soft : "rgba(255,255,255,0.85)" }}>
              {secondarySectionText}
            </div>
          </div>
        ) : null}
      </div>

      {showActions ? (
        <div className="px-3 pb-3">
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={onPrimary}
              className={clsx(
                "h-8 rounded-smx text-[12px] leading-none",
                "bg-transparent",
                accent ? "hover:bg-[#FF8000]/15 active:bg-[#FF8000]/25" : "hover:bg-white/10 active:bg-white/15",
              )}
              style={{ color: accent ? main : "#FFFFFF", border: `1px solid ${accent ? rgba(main,0.3) : "rgba(255,255,255,0.3)"}` }}
            >
              {primaryActionLabel}
            </button>
            <button
              type="button"
              onClick={onSecondary}
              className={clsx(
                "h-8 rounded-smx text-[12px] leading-none",
                "bg-transparent",
                accent ? "hover:bg-[#FF8000]/15 active:bg-[#FF8000]/25" : "hover:bg-white/10 active:bg-white/15",
              )}
              style={{ color: accent ? main : "#FFFFFF", border: `1px solid ${accent ? rgba(main,0.3) : "rgba(255,255,255,0.3)"}` }}
            >
              {secondaryActionLabel}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

// Support elements

// Mini card removed; replaced by SummaryCard below

export const DemoCardTag = ({
  className,
  children,
  variant = "pill",
  validatedLetters,
  accent = false,
  accentColor = "#FF8000",
}: {
  className?: string;
  children?: React.ReactNode;
  variant?: "pill" | "meat";
  validatedLetters?: Array<"M" | "E" | "A" | "T">;
  accent?: boolean;
  accentColor?: string;
}) => {
  const main = accent ? accentColor : "#FFFFFF";
  const weak = `rgba(${parseInt(main.slice(1,3),16)}, ${parseInt(main.slice(3,5),16)}, ${parseInt(main.slice(5,7),16)}, 0.2)`;
  if (variant === "meat") {
    return (
      <span
        className={clsx(
          "inline-flex items-center gap-2 bg-transparent px-2 py-1 text-[11px] font-light",
          className
        )}
        style={{ color: accent ? main : "rgba(255,255,255,0.85)" }}
      >
        {["M", "E", "A", "T"].map((ch) =>
          (validatedLetters || []).includes(ch as any) ? (
            <span
              key={ch}
              className="inline-flex h-5 w-5 items-center justify-center rounded-[2px]x text-[11px] leading-none"
              style={{ border: `1px solid ${accent ? weak : "rgba(255,255,255,0.2)"}` }}
            >
              {ch}
            </span>
          ) : (
            <span key={ch} className="text-[11px] leading-none">
              {ch}
            </span>
          )
        )}
      </span>
    );
  }
  return (
    <span
      className={clsx(
        "inline-block rounded-smx bg-transparent px-2 py-1 text-[11px] font-light",
        className
      )}
      style={{ border: `1px solid ${accent ? weak : "rgba(255,255,255,0.2)"}`, color: accent ? main : "rgba(255,255,255,0.85)" }}
    >
      {children}
    </span>
  );
};

// Checklist support card (matches PNG)
export const DemoChecklistCard = ({
  className,
  items = [
    { label: "Ingesting charts", checked: true },
    { label: "Analyzing data", checked: true },
    { label: "Identifying codes", checked: true },
    { label: "Compiling deltas", checked: false },
  ],
  activeIdx,
  accent = false,
  accentColor = "#FF8000",
}: {
  className?: string;
  items?: Array<{ label: string; checked: boolean }>;
  activeIdx?: number; // NEW: controls spinner location; prior items render as checks
  accent?: boolean;
  accentColor?: string;
}) => {
  const main = accent ? accentColor : "#FFFFFF";
  const weak = `rgba(${parseInt(main.slice(1,3),16)}, ${parseInt(main.slice(3,5),16)}, ${parseInt(main.slice(5,7),16)}, 0.2)`;
  const textSoft = `rgba(${parseInt(main.slice(1,3),16)}, ${parseInt(main.slice(3,5),16)}, ${parseInt(main.slice(5,7),16)}, 0.85)`;
  return (
    <div
      className={clsx(
        "w-[160px] rounded-mdx border border-white/20 bg-transparent p-3 text-white/85 font-light",
        className
      )}
      style={{ borderColor: weak, color: textSoft }}
    >
      <ul className="space-y-2">
        {items.map((it, idx) => {
          // Controlled rendering if activeIdx provided
          const controlled = typeof activeIdx === "number";
          const showSpinner = controlled ? idx === activeIdx : idx === items.length - 1 && !it.checked;
          const showCheck = controlled ? idx < (activeIdx as number) : !showSpinner && it.checked;
          return (
            <li key={it.label} className="flex items-center gap-2 text-[12px]">
              <span
                className={clsx(
                  "inline-flex h-3.5 w-3.5 items-center justify-center rounded-[2px]x border border-white/20"
                )}
                style={{ borderColor: weak }}
              >
                {showSpinner ? (
                  <ReactLoading type="spokes" color={main} height={8} width={8} />
                ) : showCheck ? (
                  <CheckIcon className="h-2.5 w-2.5" style={{ color: textSoft }} />
                ) : (
                  <span className="h-3 w-3" />
                )}
              </span>
              <span>{it.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// Document snippet card with yellow highlights (matches PNG)
export const DocSnippetCard = ({
  className,
  title = "Alice Nguyen",
  subtitle,
  text = `01-12 · PRIMARY CARE · J. Smith,\nDO · Riverside IM\nCC: [Shortness of breath];\n[diabetes follow-up]\nHPI: T2DM (poor control); [HFrEF];\n[CKD 3b]; HTN; HLD; COPD\n(stable).\n1 edema 2 wks; orthopnea 2\npillows; no chest pain.\nEXAM: BP 148/88; HR 86; Wt 101\nkg (BMI 34)\n+1 ankle edema; basilar rales;\nJVP mildly ↑\nLABS: A1c [8.7% (10/24)]; eGFR\n[42]; UACR [280 mg/g (11/24)]\nPLAN: HFrEF (mild overload) → ↑\ntorsemide 20 mg qd; low-salt; daily`,
  highlights = ["1 edema 2 wks; orthopnea 2", "pillows; no chest pain."],
  activeHighlights,
  scrollPct = 0,
  pulse = false,
  scrollRangePct = 200,
  accent = false,
  accentColor = "#FF8000",
}: {
  className?: string;
  title?: string;
  subtitle?: string;
  text?: string;
  highlights?: string[];
  activeHighlights?: string[];
  scrollPct?: number;
  pulse?: boolean;
  scrollRangePct?: number; // NEW: max vertical translate percent
  accent?: boolean;
  accentColor?: string;
}) => {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const [maxScrollPx, setMaxScrollPx] = React.useState(0);

  React.useEffect(() => {
    const compute = () => {
      const c = containerRef.current;
      const i = contentRef.current;
      if (!c || !i) return;
      const maxPx = Math.max(0, i.scrollHeight - c.clientHeight);
      setMaxScrollPx(maxPx);
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (containerRef.current) ro.observe(containerRef.current);
    if (contentRef.current) ro.observe(contentRef.current);
    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [text]);

  // Highlight phrases with animated fill
  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const renderHighlighted = (t: string, hs: string[]) => {
    if (!hs?.length) return t;
    const pattern = hs.map(escapeRegExp).join("|");
    const re = new RegExp(`(${pattern})`, "g");
    const tokens = t.split(re);
    const highlightsKey = hs.join("|");
    return tokens.map((tok, i) =>
      hs.includes(tok) ? (
        <span
          key={`h-${i}-${highlightsKey}`}
          className={clsx("inline", pulse ? "animate-docPulse" : "")}
          style={{
            backgroundImage: "linear-gradient(#F2D15F, #F2D15F)",
            backgroundRepeat: "no-repeat",
            backgroundSize: "0% 100%",
            backgroundPosition: "0 100%",
            animation: "highlightFill 900ms ease forwards",
            color: "#000",
          }}
        >
          {tok}
        </span>
      ) : (
        <span key={i}>{tok}</span>
      )
    );
  };
  const usedHighlights = activeHighlights ?? highlights;
  // Interpret scrollPct as a fraction [0..1]; compute px-based translate to avoid overscroll/empty area
  const scrollFrac = Math.max(0, Math.min(1, scrollPct));
  const translatePx = -maxScrollPx * scrollFrac;
  // Smooth-follow the target translate using an internal RAF tween to mimic prior CSS transition feel
  const targetYRef = React.useRef<number>(translatePx);
  const lastTsRef = React.useRef<number>(0);
  const rafRef = React.useRef<number | null>(null);
  const [animatedY, setAnimatedY] = React.useState<number>(translatePx);
  // Keep target in sync with props
  React.useEffect(() => {
    targetYRef.current = translatePx;
  }, [translatePx]);
  // Snap to measured target when maxScrollPx or text changes significantly (avoid initial jump after measure)
  React.useEffect(() => {
    setAnimatedY(targetYRef.current);
  }, [maxScrollPx, text]);
  // Drive smoothing loop once
  React.useEffect(() => {
    const TAU_MS = 220; // approximate the previous 220ms linear transition feel
    const tick = (now: number) => {
      const last = lastTsRef.current || now;
      const dt = Math.min(100, Math.max(0, now - last));
      lastTsRef.current = now;
      const target = targetYRef.current;
      setAnimatedY((prev) => {
        // Exponential smoothing toward target with time-constant TAU_MS
        const alpha = 1 - Math.exp(-dt / TAU_MS);
        const next = prev + (target - prev) * alpha;
        return Math.abs(next - target) < 0.05 ? target : next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, []);

  return (
    <div
      className={clsx(
        "w-[260px] rounded-mdx border border-white/20 bg-transparent p-3 text-white/90 font-light",
        className
      )}
      style={{ borderColor: accent ? `rgba(${parseInt(accentColor.slice(1,3),16)}, ${parseInt(accentColor.slice(3,5),16)}, ${parseInt(accentColor.slice(5,7),16)}, 0.2)` : undefined, color: accent ? `rgba(${parseInt(accentColor.slice(1,3),16)}, ${parseInt(accentColor.slice(3,5),16)}, ${parseInt(accentColor.slice(5,7),16)}, 0.9)` : undefined }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[12px]" style={accent ? { color: accentColor } : undefined}>{title}</div>
        {subtitle ? <div className="text-[11px] text-right ml-4 whitespace-pre-wrap" style={accent ? { color: accentColor } : undefined}>{subtitle}</div> : null}
      </div>
      <div ref={containerRef} className="whitespace-pre-wrap text-[12px] leading-4 overflow-hidden max-h-64 relative" style={{ color: accent ? `rgba(${parseInt(accentColor.slice(1,3),16)}, ${parseInt(accentColor.slice(3,5),16)}, ${parseInt(accentColor.slice(5,7),16)}, 0.85)` : undefined }}>
        <div ref={contentRef} style={{ transform: `translate3d(0, ${animatedY}px, 0)`, willChange: "transform" }}>
          {renderHighlighted(text, usedHighlights)}
        </div>
      </div>
      <style>{`
        @keyframes docPulse {
          0% { box-shadow: 0 0 0 0 rgba(242,209,95,0.5); }
          70% { box-shadow: 0 0 0 6px rgba(242,209,95,0); }
          100% { box-shadow: 0 0 0 0 rgba(242,209,95,0); }
        }
        @keyframes highlightFill {
          0% { background-size: 0% 100%; }
          100% { background-size: 100% 100%; }
        }
      `}</style>
    </div>
  );
};

// SummaryCard: smaller variant of DocSnippetCard (same behavior, reduced scale)
export const SummaryCard = ({
  className,
  title = "01-12 · PRIMARY CARE",
  text = `J. Smith, DO · Riverside IM\nCC: [Shortness of breath]; [diabetes follow-up]\nHPI: T2DM (poor control); [HFrEF]; [CKD 3b];`,
  highlights = ["J. Smith, DO · Riverside IM"],
  typingPct,
  disableHighlights = false,
  sentenceList,
  activeSentenceIds,
  accent = false,
  accentColor = "#FF8000",
}: {
  className?: string;
  title?: string;
  text?: string;
  highlights?: string[];
  typingPct?: number;
  disableHighlights?: boolean;
  sentenceList?: Array<{ id: string; text: string }>;
  activeSentenceIds?: string[];
  accent?: boolean;
  accentColor?: string;
}) => {
  const main = accent ? accentColor : "#FFFFFF";
  const weak = `rgba(${parseInt(main.slice(1,3),16)}, ${parseInt(main.slice(3,5),16)}, ${parseInt(main.slice(5,7),16)}, 0.2)`;
  const soft = `rgba(${parseInt(main.slice(1,3),16)}, ${parseInt(main.slice(3,5),16)}, ${parseInt(main.slice(5,7),16)}, 0.85)`;
  const strong = `rgba(${parseInt(main.slice(1,3),16)}, ${parseInt(main.slice(3,5),16)}, ${parseInt(main.slice(5,7),16)}, 0.9)`;
  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const renderHighlighted = (t: string, hs: string[]) => {
    if (disableHighlights || !hs?.length) return t;
    const pattern = hs.map(escapeRegExp).join("|");
    const re = new RegExp(`(${pattern})`, "g");
    const tokens = t.split(re);
    return tokens.map((tok, i) =>
      hs.includes(tok) ? (
        <span key={`h-${i}`} className="rounded-[0px] bg-[#F2D15F] px-[0px] py-[0px] text-black">
          {tok}
          </span>
      ) : (
        <span key={i}>{tok}</span>
      )
    );
  };

  const visibleText = typeof typingPct === "number"
    ? text.slice(0, Math.max(0, Math.min(text.length, Math.floor(text.length * typingPct))))
    : text;

  const renderSentences = () => {
    if (!sentenceList || sentenceList.length === 0) return (
      <div className="whitespace-pre-wrap text-[10px] leading-4" style={{ color: soft }}>
        {renderHighlighted(visibleText, highlights)}
      </div>
    );
    // Typewriter across all sentences using total characters
    const lengths = sentenceList.map((s) => s.text.length);
    const totalChars = lengths.reduce((a, b) => a + b, 0);
    const showChars = typeof typingPct === "number" ? Math.max(0, Math.min(totalChars, Math.floor(totalChars * typingPct))) : totalChars;
    const activeSet = new Set(activeSentenceIds || []);

    let consumed = 0;
    return (
      <div className="text-[10px] leading-4 space-y-1">
        {sentenceList.map((s) => {
          const len = s.text.length;
          let visible = "";
          if (showChars <= consumed) {
            visible = "";
          } else if (showChars >= consumed + len) {
            visible = s.text;
          } else {
            visible = s.text.slice(0, showChars - consumed);
          }
          consumed += len;
          const isActive = activeSet.has(s.id);
          const baseStyle = { color: isActive ? undefined : soft } as React.CSSProperties;
          return (
            <div key={s.id} className={isActive ? "bg-[#F2D15F] text-black" : ""} style={baseStyle}>
              {visible}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      className={clsx(
        "w-[180px] rounded-mdx border bg-transparent p-2 font-light",
        className
      )}
      style={{ borderColor: weak, color: strong }}
    >
      {title ? <div className="text-[11px] mb-1">{title}</div> : null}
      {renderSentences()}
    </div>
  );
};

// AccuracyCard: compact visual showing leap to ~99% with animated progress
export const AccuracyCard = ({
  className,
  fromPct = 91,
  toPct = 99,
  accent = false,
  accentColor = "#FF8000",
}: {
  className?: string;
  fromPct?: number;
  toPct?: number;
  accent?: boolean;
  accentColor?: string;
}) => {
  const main = accent ? accentColor : "#FFFFFF";
  const weak = `rgba(${parseInt(main.slice(1,3),16)}, ${parseInt(main.slice(3,5),16)}, ${parseInt(main.slice(5,7),16)}, 0.2)`;
  const soft = `rgba(${parseInt(main.slice(1,3),16)}, ${parseInt(main.slice(3,5),16)}, ${parseInt(main.slice(5,7),16)}, 0.85)`;
  const strong = `rgba(${parseInt(main.slice(1,3),16)}, ${parseInt(main.slice(3,5),16)}, ${parseInt(main.slice(5,7),16)}, 0.9)`;
  const easeOut = (t: number) => 1 - Math.pow(1 - t, 2);
  const rafRef = React.useRef<number | null>(null);
  const startRef = React.useRef<number>(0);
  const [p, setP] = React.useState(0); // 0..1 animation between fromPct and toPct
  React.useEffect(() => {
    const RAMP_MS = 2200;
    const HOLD_MS = 600;
    const CYCLE = RAMP_MS + HOLD_MS;
    startRef.current = performance.now();
    const tick = (now: number) => {
      const t = (now - startRef.current) % CYCLE;
      const ramp = Math.min(1, Math.max(0, t / RAMP_MS));
      setP(ramp);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);
  const value = Math.round(fromPct + (toPct - fromPct) * easeOut(p));
  const baseW = Math.max(0, Math.min(100, fromPct));
  const liveW = Math.max(0, Math.min(100, fromPct + (toPct - fromPct) * easeOut(p)));
  return (
    <div
      className={clsx("w-[210px] rounded-mdx border bg-transparent p-4 font-light", className)}
      style={{ borderColor: weak, color: strong }}
      role="region"
      aria-label="Accuracy Card"
    >
      <div className="text-[11px] mb-2">Accuracy</div>
      <div className="mb-2">
        <span className="text-[22px] leading-none" style={{ color: main }}>{value}%</span>
        <span className="text-[11px] ml-1" style={{ color: soft }}>target 99%</span>
      </div>
      <div className="space-y-2">
        <div>
          <div className="flex items-center justify-between text-[10px]" style={{ color: soft }}>
            <span>Baseline</span>
            <span>{Math.round(baseW)}%</span>
          </div>
          <div className="mt-1 h-2 rounded-smx bg-white/10 overflow-hidden" style={{ border: `1px solid ${weak}` }}>
            <div className="h-full" style={{ width: `${baseW}%`, backgroundColor: "rgba(255,255,255,0.35)" }} />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between text-[10px]" style={{ color: soft }}>
            <span>With VitalStrata</span>
            <span>{Math.round(liveW)}%</span>
          </div>
          <div className="mt-1 h-2 rounded-smx bg-transparent overflow-hidden" style={{ border: `1px solid ${weak}` }}>
            <div className="h-full" style={{ width: `${liveW}%`, backgroundImage: `linear-gradient(90deg, ${main}, ${main})` }} />
          </div>
        </div>
      </div>
    </div>
  );
};

// TimeToCodeCard: shows reduction in time-to-code with animated bars and headline
export const TimeToCodeCard = ({
  className,
  fromMinutes = 25.4,
  toMinutes = 6.1,
  accent = false,
  accentColor = "#FF8000",
}: {
  className?: string;
  fromMinutes?: number;
  toMinutes?: number;
  accent?: boolean;
  accentColor?: string;
}) => {
  const main = accent ? accentColor : "#FFFFFF";
  const weak = `rgba(${parseInt(main.slice(1,3),16)}, ${parseInt(main.slice(3,5),16)}, ${parseInt(main.slice(5,7),16)}, 0.2)`;
  const soft = `rgba(${parseInt(main.slice(1,3),16)}, ${parseInt(main.slice(3,5),16)}, ${parseInt(main.slice(5,7),16)}, 0.85)`;
  const strong = `rgba(${parseInt(main.slice(1,3),16)}, ${parseInt(main.slice(3,5),16)}, ${parseInt(main.slice(5,7),16)}, 0.9)`;
  const easeOut = (t: number) => 1 - Math.pow(1 - t, 2);
  const rafRef = React.useRef<number | null>(null);
  const startRef = React.useRef<number>(0);
  const [p, setP] = React.useState(0); // 0..1 animation between from and to
  React.useEffect(() => {
    const RAMP_MS = 2200;
    const HOLD_MS = 600;
    const CYCLE = RAMP_MS + HOLD_MS;
    startRef.current = performance.now();
    const tick = (now: number) => {
      const t = (now - startRef.current) % CYCLE;
      const ramp = Math.min(1, Math.max(0, t / RAMP_MS));
      setP(ramp);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);
  const liveMinutes = fromMinutes - (fromMinutes - toMinutes) * easeOut(p);
  const fmt = (m: number) => `${m.toFixed(1)}m`;
  const reductionPct = Math.max(0, Math.min(100, ((fromMinutes - toMinutes) / fromMinutes) * 100));
  const fasterX = fromMinutes / Math.max(0.01, toMinutes);
  const baseW = 100;
  const liveW = Math.max(0, Math.min(100, (liveMinutes / fromMinutes) * 100));
  return (
    <div
      className={clsx("w-[210px] rounded-mdx border bg-transparent p-4 font-light", className)}
      style={{ borderColor: weak, color: strong }}
      role="region"
      aria-label="Time To Code Card"
    >
      <div className="text-[11px] mb-2">Time-to-code</div>
      <div className="mb-2">
        <span className="text-[20px] leading-none" style={{ color: main }}>{fmt(liveMinutes)}</span>
        <span className="text-[11px] ml-1" style={{ color: soft }}>
          ↓{Math.round(reductionPct)}% · {fasterX.toFixed(1)}x faster
        </span>
      </div>
      <div className="space-y-2">
        <div>
          <div className="flex items-center justify-between text-[10px]" style={{ color: soft }}>
            <span>Baseline</span>
            <span>{fmt(fromMinutes)}</span>
          </div>
          <div className="mt-1 h-2 rounded-smx bg-white/10 overflow-hidden" style={{ border: `1px solid ${weak}` }}>
            <div className="h-full" style={{ width: `${baseW}%`, backgroundColor: "rgba(255,255,255,0.35)" }} />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between text-[10px]" style={{ color: soft }}>
            <span>With VitalStrata</span>
            <span>{fmt(liveMinutes)}</span>
          </div>
          <div className="mt-1 h-2 rounded-smx bg-transparent overflow-hidden" style={{ border: `1px solid ${weak}` }}>
            <div className="h-full" style={{ width: `${liveW}%`, backgroundImage: `linear-gradient(90deg, ${main}, ${main})` }} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ModelSupportCard: shows dual support for V24 and V28 with animated emphasis
export const ModelSupportCard = ({
  className,
  accent = false,
  accentColor = "#FF8000",
}: {
  className?: string;
  accent?: boolean;
  accentColor?: string;
}) => {
  const main = accent ? accentColor : "#FFFFFF";
  const weak = `rgba(${parseInt(main.slice(1,3),16)}, ${parseInt(main.slice(3,5),16)}, ${parseInt(main.slice(5,7),16)}, 0.2)`;
  const soft = `rgba(${parseInt(main.slice(1,3),16)}, ${parseInt(main.slice(3,5),16)}, ${parseInt(main.slice(5,7),16)}, 0.85)`;
  const strong = `rgba(${parseInt(main.slice(1,3),16)}, ${parseInt(main.slice(3,5),16)}, ${parseInt(main.slice(5,7),16)}, 0.9)`;
  // Toggle PY: 2025 (67%/33%) vs 2026 (100%/0%)
  const [py, setPy] = React.useState<"2025" | "2026">("2025");
  const v28Weight = py === "2025" ? 0.67 : 1.0;
  const v24Weight = py === "2025" ? 0.33 : 0.0;
  // Sample RAFs
  const v28Raf = 1.23, v24Raf = 1.16;
  const blendedRaf = v28Weight * v28Raf + v24Weight * v24Raf;
  const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
  const widthForRaf = (raf: number) => {
    const norm = clamp01((raf - 1.0) / 0.3); // assume 1.0–1.3 range
    return 50 + 40 * norm; // 50%–90%
  };
  const blendedW = widthForRaf(blendedRaf);
  const v28SegW = v28Weight * 100;
  const v24SegW = v24Weight * 100;
  return (
    <div
      className={clsx("w-[210px] rounded-mdx border bg-transparent p-4 font-light", className)}
      style={{ borderColor: weak, color: strong }}
      role="region"
      aria-label="Model Support Card"
    >
      <div className="text-[11px] mb-2">CMS-HCC Models</div>
      <div className="inline-flex rounded-smx border mb-3 overflow-hidden" role="tablist" aria-label="Performance Year" style={{ border: `1px solid ${weak}` }}>
        <button
          type="button"
          role="tab"
          aria-selected={py === "2025"}
          onClick={() => setPy("2025")}
          className={clsx("text-[10px] px-2 py-1", py === "2025" ? "bg-white text-black" : "")}
          style={{ color: py === "2025" ? "#000" : soft, backgroundColor: py === "2025" ? main : "transparent" }}
        >
          PY2025
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={py === "2026"}
          onClick={() => setPy("2026")}
          className={clsx("text-[10px] px-2 py-1", py === "2026" ? "bg-white text-black" : "")}
          style={{ color: py === "2026" ? "#000" : soft, backgroundColor: py === "2026" ? main : "transparent" }}
        >
          PY2026
        </button>
      </div>
      {/* PY weights (stacked bar) */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[10px]" style={{ color: soft }}>
          <span>PY weights</span>
          <span>
            V28 {Math.round(v28Weight * 100)}% · V24 {Math.round(v24Weight * 100)}%
          </span>
        </div>
        <div className="mt-1 h-2 rounded-smx overflow-hidden flex" style={{ border: `1px solid ${weak}` }}>
          <div className="h-full" style={{ width: `${v28SegW}%`, backgroundImage: `linear-gradient(90deg, ${main}, ${main})`, transition: "width 260ms ease-out" }} />
          <div className="h-full" style={{ width: `${v24SegW}%`, backgroundColor: "rgba(255,255,255,0.35)", transition: "width 260ms ease-out" }} />
        </div>
        <div className="mt-1 text-[9px]" style={{ color: soft }}>
          {py === "2025" ? "Blend: 67% V28 · 33% V24" : "V28-only"}
        </div>
      </div>
      {/* Blended RAF */}
      <div>
        <div className="flex items-center justify-between text-[10px]" style={{ color: soft }}>
          <span className="inline-flex items-center gap-1">
            <CheckIcon className="h-3 w-3" style={{ color: soft }} />
            Blended RAF
          </span>
          <span>{blendedRaf.toFixed(2)}</span>
        </div>
        <div className="mt-1 h-2 rounded-smx bg-transparent overflow-hidden" style={{ border: `1px solid ${weak}` }}>
          <div className="h-full" style={{ width: `${blendedW}%`, backgroundImage: `linear-gradient(90deg, ${main}, ${main})`, transition: "width 260ms ease-out" }} />
        </div>
        {/* <div className="mt-1 text-[9px]" style={{ color: soft }}>
          Auto-calculated from V28/V24 by PY
        </div> */}
      </div>
    </div>
  );
};

export default DemoCard;


